import React, { useEffect, useState } from 'react';
import Table, { ColumnConfig } from 'components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { Icon } from 'components/ui/icon';
import PageHeading from 'components/heading';
import Pagination from 'components/pagination';
import Search from 'components/search';
import ExportBtn from 'components/modals/export/exportBtn';
import ToastContainer, { ToastType } from 'components/toasts/container';
import usePartnerStores from 'state/partnerStores/hooks/usePartnerStores';
import CURRENCY from 'utils/currency';
import useQuery from 'utils/hooks/useQuery';
import { downloadFile } from 'utils/download';
import { useLocation } from 'react-router-dom';
import { PARTNER_STORES } from 'state/partnerStores/type';

const PartnerStores = (): JSX.Element => {
  const location = useLocation();
  const queryParams = location?.search;
  const query = useQuery(queryParams);

  const { partnerStores, toFetchStores, toExportStores } = usePartnerStores();
  const { list } = partnerStores;

  const searchString = query.get('search') || undefined;

  // Next and prev key
  const [prevKey, setPrevKey] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastType | false>(false);
  const [numResults, setNumResults] = useState(0);

  // get to prev page
  const prevPage = () => {
    toFetchStores({
      key: prevKey,
      search_string: searchString,
    });
  };

  // get to next page
  const nextPage = () => {
    toFetchStores({
      key: nextKey,
      search_string: searchString,
    });
  };

  useEffect(() => {
    toFetchStores({
      search_string: searchString,
    });
  }, [queryParams]);

  useEffect(() => {
    if (list && list?.items?.length > 0) {
      // setting next and prev key
      setNextKey(list.next_key);
      setPrevKey(list.prev_key);

      setNumResults(list.total_count);
    } else {
      setNumResults(0);
    }
  }, [list]);

  const columns: ColumnConfig<PARTNER_STORES.IListItem>[] = [
    {
      key: 'store_name',
      label: 'STORE',
      sortable: true,
      renderCell: (row) => <div data-testid="store_name">{row.store_name}</div>,
    },
    {
      key: 'store_id',
      label: 'STORE ID',
      sortable: true,
      renderCell: (row) => (
        <div data-testid="store_id">{row.store_id || ''}</div>
      ),
    },
    {
      key: 'fee_per_transaction',
      label: 'FEE PER TRANSACTION',
      sortable: true,
      renderCell: (row) => (
        <div
          data-testid="fee_per_transaction"
          className={'text-gray-blue-60 font-medium'}
        >
          {CURRENCY.convertToMainUnit(row.fee_per_transaction)}
        </div>
      ),
      renderHeader: () => (
        <div className="flex items-center">
          <span>FEE PER TRANSACTION</span>
          <span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="pl-1">
                    <Icon name="info" size={16} className="text-gray-blue-60" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  This is the fee charged per transaction for a particular
                  store.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
      ),
    },
    {
      key: 'lifetime_value',
      label: 'LIFETIME VALUE',
      sortable: true,
      renderCell: (row) => (
        <div
          data-testid="lifetime_value"
          className={'text-gray-blue-60 font-medium'}
        >
          {CURRENCY.convertToMainUnit(row.lifetime_value)}
        </div>
      ),
      renderHeader: () => (
        <div className="flex items-center">
          <span>LIFETIME VALUE</span>
          <span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="pl-1">
                    <Icon name="info" size={16} className="text-gray-blue-60" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  This is total earnings from a particular store till date.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
      ),
    },
  ];

  const exportFee = async () => {
    const data = await toExportStores();
    if (data && 'url' in data) {
      downloadFile(data.url, 'stores-list', 'csv');
      setToast(ToastType.success);
    } else {
      setToast(ToastType.error);
    }
  };

  return (
    <>
      <PageHeading heading="Stores" className="mb-7" />
      <PageHeading
        heading="Fees"
        subHeading="View the details of fee and it’s type charged for each store per transaction"
        className="mb-5"
        level="h2"
      />
      <div className="flex mb-3">
        <span className="mr-auto">
          <Search queryParams={queryParams} />
        </span>

        <ExportBtn onClick={exportFee} id="tnx-export-btn" />
      </div>
      <Table
        id="partner-stores"
        data={list?.items || []}
        columns={columns}
        noDataIndication={{
          heading: 'No stores to show',
          subText: 'No partner-stores have been onboarded and connected yet.',
        }}
      />
      <Pagination
        results={numResults}
        prevPage={prevPage}
        nextPage={nextPage}
        prevKey={prevKey}
        nextKey={nextKey}
      />
      {Boolean(toast) && (
        <ToastContainer
          showToast={Boolean(toast)}
          onClose={() => setToast(false)}
          message={
            toast === ToastType.success
              ? 'Your export is ready!'
              : toast === ToastType.error
              ? 'Something went wrong'
              : ''
          }
          type={toast || undefined}
          id="export"
        />
      )}
    </>
  );
};

export default PartnerStores;
